import { makeAutoObservable, runInAction } from 'mobx';
import { nanoid } from 'nanoid';
import dbManager from '../utils/IndexedDBManager';

// 持久化存储键名
const STORAGE_KEYS = {
    SNAPSHOT_IDS: 'videoCapture_snapshotIds', // 存储截图ID列表
    CURRENT_SNAPSHOT: 'videoCapture_currentSnapshot',
    VIDEO_TIMESTAMP: 'videoCapture_timestamp'
};

// 最大存储截图数量
const MAX_SNAPSHOTS = 50;

class VideoCapture {
    videoFile = null; // 当前加载的视频文件
    videoUrl = ''; // 视频的URL
    videoElement = null; // 视频DOM元素引用
    snapshots = []; // 保存的视频截图列表
    currentSnapshot = null; // 当前选中的截图
    isPlaying = false; // 视频是否正在播放
    error = null; // 错误信息
    lastVideoTimestamp = 0; // 最后的视频时间点
    isLoading = false; // 是否正在加载数据

    constructor() {
        makeAutoObservable(this);
        // 尝试从存储中恢复状态
        this.restoreState();
    }

    // 从存储中恢复状态
    async restoreState() {
        try {
            this.isLoading = true;
            
            // 1. 从 IndexedDB 恢复所有截图
            try {
                const storedSnapshots = await dbManager.getAllSnapshots();
                if (storedSnapshots && storedSnapshots.length > 0) {
                    // 按时间戳排序
                    storedSnapshots.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                    runInAction(() => {
                        this.snapshots = storedSnapshots;
                    });
                }
            } catch (dbErr) {
                console.error("从IndexedDB恢复截图失败:", dbErr);
                // 失败时尝试从localStorage获取截图ID列表
                try {
                    const snapshotIds = localStorage.getItem(STORAGE_KEYS.SNAPSHOT_IDS);
                    if (snapshotIds) {
                        const parsedIds = JSON.parse(snapshotIds);
                        console.log("从localStorage恢复截图ID列表:", parsedIds);
                    }
                } catch (lsErr) {
                    console.error("从localStorage恢复截图ID列表失败:", lsErr);
                }
            }

            // 2. 恢复当前选中的截图
            const currentSnapshotId = localStorage.getItem(STORAGE_KEYS.CURRENT_SNAPSHOT);
            if (currentSnapshotId && this.snapshots.length > 0) {
                runInAction(() => {
                    this.currentSnapshot = this.snapshots.find(s => s.id === currentSnapshotId) || this.snapshots[0];
                });
            }

            // 3. 恢复视频时间点
            const lastTimestamp = localStorage.getItem(STORAGE_KEYS.VIDEO_TIMESTAMP);
            if (lastTimestamp) {
                runInAction(() => {
                    this.lastVideoTimestamp = parseFloat(lastTimestamp) || 0;
                });
            }
        } catch (err) {
            console.error("恢复视频状态出错:", err);
            // 恢复失败则重置状态
            runInAction(() => {
                this.snapshots = [];
                this.currentSnapshot = null;
                this.lastVideoTimestamp = 0;
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    // 保存状态
    async saveState() {
        try {
            // 1. 将所有截图保存到 IndexedDB
            if (this.snapshots.length > 0) {
                // 限制截图数量
                const snapshotsToSave = [...this.snapshots];
                if (snapshotsToSave.length > MAX_SNAPSHOTS) {
                    // 删除多余的旧截图 (从IndexedDB中)
                    const toDelete = snapshotsToSave.slice(0, snapshotsToSave.length - MAX_SNAPSHOTS);
                    for (const snapshot of toDelete) {
                        await dbManager.deleteSnapshot(snapshot.id);
                    }
                    
                    // 更新内存中的集合
                    runInAction(() => {
                        this.snapshots = this.snapshots.slice(-MAX_SNAPSHOTS);
                    });
                }
                
                // 保存截图列表
                await dbManager.saveSnapshots(this.snapshots);
                
                // 将截图ID列表保存到localStorage (用于快速恢复)
                const snapshotIds = this.snapshots.map(s => s.id);
                localStorage.setItem(STORAGE_KEYS.SNAPSHOT_IDS, JSON.stringify(snapshotIds));
            } else {
                // 如果没有截图，清空存储
                await dbManager.clearAllSnapshots();
                localStorage.removeItem(STORAGE_KEYS.SNAPSHOT_IDS);
            }
            
            // 2. 保存当前选中的截图 ID
            if (this.currentSnapshot) {
                localStorage.setItem(STORAGE_KEYS.CURRENT_SNAPSHOT, this.currentSnapshot.id);
            } else {
                localStorage.removeItem(STORAGE_KEYS.CURRENT_SNAPSHOT);
            }

            // 3. 保存视频时间点
            if (this.videoElement) {
                this.lastVideoTimestamp = this.videoElement.currentTime;
                localStorage.setItem(STORAGE_KEYS.VIDEO_TIMESTAMP, this.lastVideoTimestamp.toString());
            }
        } catch (err) {
            console.error("保存视频状态出错:", err);
            // 出错时尝试只保存元数据到localStorage
            try {
                if (this.snapshots.length > 0) {
                    const snapshotIds = this.snapshots.map(s => s.id);
                    localStorage.setItem(STORAGE_KEYS.SNAPSHOT_IDS, JSON.stringify(snapshotIds));
                }
                
                if (this.currentSnapshot) {
                    localStorage.setItem(STORAGE_KEYS.CURRENT_SNAPSHOT, this.currentSnapshot.id);
                }
                
                if (this.videoElement) {
                    localStorage.setItem(STORAGE_KEYS.VIDEO_TIMESTAMP, this.videoElement.currentTime.toString());
                }
            } catch (secondErr) {
                console.error("保存元数据到localStorage也失败:", secondErr);
            }
        }
    }

    // 设置视频文件
    setVideoFile(file) {
        try {
            if (this.videoUrl && this.videoUrl.startsWith('blob:')) {
                URL.revokeObjectURL(this.videoUrl);
            }
            
            this.videoFile = file;
            
            // 为文件创建一个安全的URL（需要注意CSP限制）
            if (file) {
                const url = URL.createObjectURL(file);
                
                runInAction(() => {
                    this.videoUrl = url;
                    this.isPlaying = false;
                    this.error = null;
                });
            } else {
                runInAction(() => {
                    this.videoUrl = '';
                    this.isPlaying = false;
                });
            }
        } catch (err) {
            console.error("设置视频文件出错:", err);
            runInAction(() => {
                this.error = err.message || "无法加载视频文件";
                this.videoUrl = '';
                this.isPlaying = false;
            });
            throw err;
        }
    }

    // 设置视频DOM元素引用
    setVideoElement(element) {
        this.videoElement = element;
        
        // 添加事件监听
        if (element) {
            // 设置之前保存的时间点
            if (this.lastVideoTimestamp > 0) {
                try {
                    // 等待视频元数据加载完成
                    const setTimeHandler = () => {
                        try {
                            element.currentTime = this.lastVideoTimestamp;
                        } catch (e) {
                            console.warn("设置视频时间点失败:", e);
                        }
                        element.removeEventListener('loadedmetadata', setTimeHandler);
                    };
                    
                    element.addEventListener('loadedmetadata', setTimeHandler);
                } catch (e) {
                    console.warn("设置视频时间点监听失败:", e);
                }
            }
            
            element.addEventListener('play', () => {
                runInAction(() => {
                    this.isPlaying = true;
                });
            });
            
            element.addEventListener('pause', () => {
                runInAction(() => {
                    this.isPlaying = false;
                    // 视频暂停时保存状态
                    this.saveState();
                });
            });
            
            element.addEventListener('ended', () => {
                runInAction(() => {
                    this.isPlaying = false;
                    this.saveState();
                });
            });
            
            // 每当时间更新时，更新最后的时间戳
            element.addEventListener('timeupdate', () => {
                this.lastVideoTimestamp = element.currentTime;
            });
        }
    }

    // 播放/暂停视频 - 由于我们现在使用原生控件，这个方法可能不太需要
    togglePlay() {
        if (!this.videoElement) return;
        
        try {
            if (this.isPlaying) {
                this.videoElement.pause();
            } else {
                const playPromise = this.videoElement.play();
                
                // 处理播放承诺，防止在某些浏览器中的错误
                if (playPromise !== undefined) {
                    playPromise.catch(err => {
                        console.error("视频播放错误:", err);
                        runInAction(() => {
                            this.error = "视频播放失败，可能是由于浏览器安全策略限制";
                            this.isPlaying = false;
                        });
                    });
                }
            }
        } catch (err) {
            console.error("播放/暂停视频出错:", err);
            runInAction(() => {
                this.error = err.message || "无法控制视频播放";
                this.isPlaying = false;
            });
        }
    }

    // 添加截图到列表
    async addSnapshot(snapshot) {
        try {
            // 保存到IndexedDB
            await dbManager.saveSnapshot(snapshot);
            
            runInAction(() => {
                // 限制内存中的截图数量
                if (this.snapshots.length >= MAX_SNAPSHOTS) {
                    this.snapshots.shift(); // 移除最旧的截图
                }
                
                this.snapshots.push(snapshot);
                this.currentSnapshot = snapshot;
                this.error = null;
            });
            
            // 保存状态到IndexedDB和localStorage
            await this.saveState();
            
            return snapshot;
        } catch (err) {
            console.error("添加截图失败:", err);
            runInAction(() => {
                this.error = err.message || "无法保存截图";
            });
            throw err;
        }
    }

    // 选择一个现有的截图
    async selectSnapshot(id) {
        const snapshot = this.snapshots.find(snap => snap.id === id);
        if (snapshot) {
            runInAction(() => {
                this.currentSnapshot = snapshot;
            });
            await this.saveState();
            return true;
        }
        return false;
    }

    // 删除一个截图
    async deleteSnapshot(id) {
        try {
            const index = this.snapshots.findIndex(snap => snap.id === id);
            if (index !== -1) {
                // 从IndexedDB中删除
                await dbManager.deleteSnapshot(id);
                
                runInAction(() => {
                    this.snapshots.splice(index, 1);
                    // 如果删除的是当前选中的截图，重置选中状态
                    if (this.currentSnapshot && this.currentSnapshot.id === id) {
                        this.currentSnapshot = this.snapshots.length > 0 ? this.snapshots[0] : null;
                    }
                });
                
                await this.saveState();
                return true;
            }
            return false;
        } catch (err) {
            console.error("删除截图失败:", err);
            runInAction(() => {
                this.error = err.message || "无法删除截图";
            });
            return false;
        }
    }

    // 清空所有截图
    async clearSnapshots() {
        try {
            // 清空IndexedDB
            await dbManager.clearAllSnapshots();
            
            runInAction(() => {
                this.snapshots = [];
                this.currentSnapshot = null;
            });
            
            await this.saveState();
        } catch (err) {
            console.error("清空截图失败:", err);
            runInAction(() => {
                this.error = err.message || "无法清空截图";
            });
        }
    }

    // 跳转到视频指定时间
    seekToTime(time) {
        if (!this.videoElement) return;
        try {
            this.videoElement.currentTime = Math.max(0, Math.min(time, this.videoElement.duration));
            this.lastVideoTimestamp = this.videoElement.currentTime;
            this.saveState();
        } catch (err) {
            console.error("跳转视频时间出错:", err);
            runInAction(() => {
                this.error = err.message || "无法跳转到指定时间";
            });
        }
    }

    // 重置状态
    async reset() {
        try {
            if (this.videoUrl && this.videoUrl.startsWith('blob:')) {
                URL.revokeObjectURL(this.videoUrl);
            }
            
            // 清空IndexedDB中的数据
            await dbManager.clearAllSnapshots();
            
            runInAction(() => {
                this.videoFile = null;
                this.videoUrl = '';
                this.isPlaying = false;
                this.error = null;
                this.snapshots = [];
                this.currentSnapshot = null;
                this.lastVideoTimestamp = 0;
            });
            
            // 清除本地存储
            localStorage.removeItem(STORAGE_KEYS.SNAPSHOT_IDS);
            localStorage.removeItem(STORAGE_KEYS.CURRENT_SNAPSHOT);
            localStorage.removeItem(STORAGE_KEYS.VIDEO_TIMESTAMP);
        } catch (err) {
            console.error("重置视频状态出错:", err);
        }
    }
}

const videoCapture = new VideoCapture();
export default videoCapture; 